/**
 * The time maths of the music video effects (`musicVideoEffects.ts`). The Remotion render and the
 * browser preview of the editor use the same functions, so a piece shows at the same time with the
 * same strength in both. Pure; no heavy imports.
 */
import { MUSIC_VIDEO_EFFECTS, type MusicVideoEffect, type MusicVideoEffectKind } from './musicVideoEffects';

/** The default fade in and fade out of an effect, in seconds. */
export const MUSIC_VIDEO_EFFECT_FADE_SEC = 0.25;
/** The default time a beat pulse takes to fall from 1 to 0, in seconds. */
export const MUSIC_VIDEO_BEAT_DECAY_SEC = 0.25;

type EffectTimes = Pick<MusicVideoEffect, 'startSec' | 'endSec'>;

/**
 * How far `sec` is through the effect: 0 at the start, 1 at the end. Null before the start and
 * after the end.
 */
export function effectProgress(effect: EffectTimes, sec: number): number | null {
  if (!Number.isFinite(sec) || sec < effect.startSec || sec > effect.endSec) {
    return null;
  }
  const lengthSec = effect.endSec - effect.startSec;
  if (lengthSec <= 0) {
    return 1;
  }
  return Math.min(1, Math.max(0, (sec - effect.startSec) / lengthSec));
}

/**
 * The strength of the effect at `sec` (0..1): 0 outside and at the edges, a linear rise over
 * `fadeSec` after the start, 1 in the middle, a linear fall over `fadeSec` before the end. A short
 * effect fades over half its length at most.
 */
export function effectEnvelope(effect: EffectTimes, sec: number, fadeSec = MUSIC_VIDEO_EFFECT_FADE_SEC): number {
  if (effectProgress(effect, sec) === null) {
    return 0;
  }
  const lengthSec = effect.endSec - effect.startSec;
  const fade = Math.min(Math.max(0, fadeSec), lengthSec / 2);
  if (!(fade > 0)) {
    return 1;
  }
  const rise = (sec - effect.startSec) / fade;
  const fall = (effect.endSec - sec) / fade;
  return Math.min(1, Math.max(0, Math.min(rise, fall)));
}

/**
 * The beat pulse at `sec` (0..1): 1 on a beat, then a quadratic fall to 0 over `decaySec`. 0 before
 * the first beat, between beats after the decay, and with no beats. `beats` are the sorted beat
 * times of the song map, in seconds.
 */
export function beatPulse(sec: number, beats: readonly number[], decaySec = MUSIC_VIDEO_BEAT_DECAY_SEC): number {
  if (beats.length === 0 || !Number.isFinite(sec) || !(decaySec > 0)) {
    return 0;
  }
  let low = 0;
  let high = beats.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if ((beats[middle] ?? Infinity) <= sec) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }
  const beat = beats[low - 1];
  if (beat === undefined) {
    return 0;
  }
  const t = (sec - beat) / decaySec;
  if (t >= 1) {
    return 0;
  }
  return (1 - t) * (1 - t);
}

/**
 * The effects that play at `sec`: the start is inside and the end is not, so two effects that touch
 * do not both play at the edge. The order of the list stays.
 */
export function effectsAt(effects: readonly MusicVideoEffect[], sec: number): MusicVideoEffect[] {
  return effects.filter((effect) => sec >= effect.startSec && sec < effect.endSec);
}

/**
 * The texts of the video itself go out this long before an effect that shows the same kind of text
 * and come back this long after it: the two never cross-fade in the same place.
 */
export const MUSIC_VIDEO_TEXT_YIELD_SEC = 0.3;

/** The effect kinds whose card shows the song title or the artist name (an empty text gets the song value). */
export const SONG_INFO_CARD_KINDS: ReadonlySet<MusicVideoEffectKind> = new Set(['titleCard', 'artistCard']);

/** True for a lyric moment (family `lyric`): it shows the words of the line itself. */
export function isLyricMomentEffect(effect: Pick<MusicVideoEffect, 'kind'>): boolean {
  return MUSIC_VIDEO_EFFECTS[effect.kind].family === 'lyric';
}

/** True for a title card or an artist card: it shows the song title or the artist name. */
export function isSongInfoCardEffect(effect: Pick<MusicVideoEffect, 'kind'>): boolean {
  return SONG_INFO_CARD_KINDS.has(effect.kind);
}

/**
 * The opacity (0..1) of a text of the video that yields to the effects that `yieldsTo` picks: 0
 * while such an effect plays, a linear fall over `fadeSec` before its start and a linear rise over
 * `fadeSec` after its end, else 1. So the text is gone before the effect comes in and comes back
 * only after the effect left: the two never show at the same time. Pure.
 */
export function textYieldOpacity(
  effects: readonly MusicVideoEffect[],
  sec: number,
  yieldsTo: (effect: MusicVideoEffect) => boolean,
  fadeSec = MUSIC_VIDEO_TEXT_YIELD_SEC
): number {
  let opacity = 1;
  for (const effect of effects) {
    if (!yieldsTo(effect) || !(effect.endSec > effect.startSec)) {
      continue;
    }
    const outside = sec < effect.startSec ? effect.startSec - sec : sec >= effect.endSec ? sec - effect.endSec : 0;
    const value = fadeSec > 0 ? Math.min(1, outside / fadeSec) : outside > 0 ? 1 : 0;
    opacity = Math.min(opacity, value);
  }
  return Math.round(Math.max(0, opacity) * 1000) / 1000;
}

/**
 * The opacity of the lyric captions at `sec` (the render and the editor preview): 0 while a lyric
 * moment plays (it shows the line itself), with the yield fade around it. At most one lyric line
 * shows on a frame. Pure.
 */
export function captionOpacityAt(effects: readonly MusicVideoEffect[], sec: number): number {
  return textYieldOpacity(effects, sec, isLyricMomentEffect);
}

/**
 * The opacity of the song info texts of the video at `sec` (the title card, the name, the
 * "Artist · Title" corner): 0 while a title card or an artist card effect plays, with the yield
 * fade around it. The title never shows twice. Pure.
 */
export function songInfoOpacityAt(effects: readonly MusicVideoEffect[], sec: number): number {
  return textYieldOpacity(effects, sec, isSongInfoCardEffect);
}
